import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft, Info, AlertTriangle, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Login notification state
  const [loginNotification, setLoginNotification] = useState(null);
  
  // Fetch login notification on mount
  useEffect(() => {
    const fetchNotification = async () => {
      try {
        const response = await fetch(`${API}/public/site-settings/login-notification`);
        const data = await response.json();
        if (data.enabled && data.message) {
          setLoginNotification(data);
        }
      } catch (err) {
        console.error("Failed to fetch login notification:", err);
      }
    };
    fetchNotification();
  }, []);
  
  // Check localStorage for existing user session and redirect if logged in
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        // User data exists in localStorage, redirect based on role
        if (userData.role === "admin" || userData.role === "manager") {
          navigate("/admin", { replace: true });
        } else {
          navigate("/app/profile", { replace: true });
        }
      } catch (e) {
        // Invalid stored data, clear it
        localStorage.removeItem("user");
      }
    }
  }, [navigate]);
  
  // Get return URL from state (passed from creator profile page) or sessionStorage (fallback)
  const returnUrl = location.state?.from || sessionStorage.getItem('returnUrl') || null;
  
  // Store return URL in sessionStorage for persistence across page reloads
  if (location.state?.from) {
    sessionStorage.setItem('returnUrl', location.state.from);
  }
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/unified-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",  // Include cookies in request/response
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Login failed");
      }

      // Store only user info in localStorage (token is in httpOnly cookie)
      localStorage.setItem("user", JSON.stringify({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        user_type: data.user_type,
        credits: data.credits || 0
      }));

      // Redirect based on user type, but check for return URL first
      let redirectPath;
      if (returnUrl && data.user_type !== "admin") {
        // Redirect back to creator profile if coming from there (only for fans/creators)
        redirectPath = returnUrl;
        // Clear the stored return URL
        sessionStorage.removeItem('returnUrl');
      } else {
        redirectPath = data.redirect || (data.user_type === "admin" ? "/admin" : "/app/profile");
      }
      window.location.href = redirectPath;
    } catch (err) {
      if (err.message === "Load failed" || err.message === "Failed to fetch") {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[var(--card)] rounded-2xl p-8 max-w-md w-full border border-[var(--border)] shadow-xl"
      >
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <a href="/" className="text-2xl font-bold inline-block" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
            <span className="text-[var(--foreground)]">HotPeek</span>
          </a>
          <p className="text-[var(--muted-foreground)] mt-2">Welcome back</p>
        </div>

        {/* Admin Notification */}
        {loginNotification && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-lg p-4 mb-6 flex items-start gap-3 ${
              loginNotification.type === "info" 
                ? "bg-cyan-500/10 border border-cyan-500/30"
                : loginNotification.type === "warning"
                  ? "bg-amber-500/10 border border-amber-500/30"
                  : "bg-green-500/10 border border-green-500/30"
            }`}
            data-testid="login-notification"
          >
            {loginNotification.type === "info" ? (
              <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 text-cyan-400`} />
            ) : loginNotification.type === "warning" ? (
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 text-amber-400`} />
            ) : (
              <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 text-green-400`} />
            )}
            <p className={`text-sm ${
              loginNotification.type === "info" 
                ? "text-cyan-400"
                : loginNotification.type === "warning"
                  ? "text-amber-400"
                  : "text-green-400"
            }`}>
              {loginNotification.message}
            </p>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-100 border border-red-300 rounded-lg p-3 mb-6 flex items-center gap-2"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-600 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-[var(--foreground)] text-sm mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--muted-foreground)]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full bg-[var(--background)] border border-[var(--border)] rounded-lg py-3 pl-10 pr-4 text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] transition-colors"
                data-testid="login-email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-[var(--foreground)] text-sm mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--muted-foreground)]" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full bg-[var(--background)] border border-[var(--border)] rounded-lg py-3 pl-10 pr-12 text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] transition-colors"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-[var(--primary)] hover:underline transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--primary)] text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Signing In...
              </span>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Register Link */}
        <p className="text-center text-[var(--muted-foreground)] mt-6">
          Don't have an account?{" "}
          <Link 
            to="/register" 
            state={returnUrl ? { from: returnUrl } : undefined}
            className="text-[var(--primary)] hover:underline transition-colors"
          >
            Create one
          </Link>
        </p>

        {/* Back to Home */}
        <Link
          to="/"
          className="flex items-center justify-center gap-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] mt-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </motion.div>
    </div>
  );
};

export default LoginPage;
